import React, { useState, useEffect } from 'react';
import api from '../services/api';
import CopyBtn from './CopyBtn';
import './PositionDetailModal.css';
import './TokenModal.css'; // 🆕 Import des styles pour les sections d'évaluation

const PositionDetailModal = ({ position, onClose }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [copied, setCopied] = useState(false); // 🆕 State pour le bouton de copie

  // 🆕 Fonction pour formater la source (comme TokenList.js)
  const formatSource = (src) => {
    if (!src) return 'N/A';
    const s = String(src).toLowerCase();
    if (s.includes('cheatmode')) return '🎯 Cheatmode';
    if (s.includes('moralis')) return '🔮 Moralis';
    if (s.includes('solana')) return '⚡ Solana';
    if (s.includes('boost') || s.includes('dex')) return '🚀 Boosted';
    if (s.includes('evm') || s.includes('quicknode') || s.includes('bsc') || s.includes('eth') || s.includes('base') || s.includes('arbitrum')) return '🔷 EVM';
    if (s.includes('telegram')) return '📱 Telegram';
    if (s.includes('raydium')) return '🌊 Raydium';
    if (s.includes('pool')) return '💧 Pool';
    if (s.includes('pair')) return '🔗 Pair';
    return src;
  };

  useEffect(() => {
    fetchPositionDetails();
  }, [position.id]);

  const fetchPositionDetails = async () => {
    try {
      setLoading(true);

      const response = await api.get(`/positions/${position.id}/trades`);
      console.log('Position details:', response.data);

      setTrades(response.data.trades || []);
      setSummary(response.data.summary || null);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      console.error('Error details:', error.response?.data || error.message);
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return '$0.000000';
    if (price >= 1) return `$${price.toFixed(2)}`;
    if (price >= 0.01) return `$${price.toFixed(4)}`;
    if (price >= 0.0001) return `$${price.toFixed(6)}`;
    return `$${price.toExponential(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTradeIcon = (type) => {
    switch (type) {
      case 'buy': return '🟢';
      case 'take_profit': return '💰';
      case 'stop_loss': return '🛑';
      case 'sell_partial': return '🔵';
      case 'sell_complete': return '🔴';
      default: return '⚪';
    }
  };

  const getTradeLabel = (trade) => {
    if (trade.trade_type === 'buy') return 'ACHAT INITIAL';
    if (trade.trade_type === 'take_profit') {
      if (trade.reason && trade.reason.includes('take_profit_')) {
        const percent = trade.reason.split('_').pop();
        return `TAKE PROFIT +${percent}%`;
      }
      return 'TAKE PROFIT';
    }
    if (trade.trade_type === 'stop_loss') return 'STOP LOSS';
    if (trade.trade_type === 'sell_partial') return 'VENTE PARTIELLE';
    if (trade.trade_type === 'sell_complete') return 'VENTE COMPLÈTE';
    return trade.trade_type.toUpperCase();
  };

  // 🆕 Fonction pour copier l'adresse du token
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(position.token_address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erreur copie:', err);
    }
  };

  if (!position) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📊 Détails de la Position</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Informations de la position */}
          <div className="position-info-section">
            {/* Token Name and DexScreener Link */}
            <div className="token-header">
              <div className="token-name-section">
                <h3 className="token-name">
                  {position.token_name || position.token_symbol || 'Token'}
                </h3>
                <span className="token-symbol">{position.token_symbol || ''}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <a
                  href={`https://gmgn.ai/sol/token/${position.ui_mint || position.token_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dexscreener-link"
                  title="Voir sur GMGN"
                >
                  🔍 GMGN
                </a>
                <a
                  href={`https://dexscreener.com/${position.chain}/${position.ui_mint || position.token_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dexscreener-link"
                  title="Voir sur DexScreener"
                >
                  📊 DexScreener
                </a>
              </div>
            </div>

            <div className="info-grid">
              <div className="info-card info-card-token-address">
                <div className="info-label">Token Address</div>
                <div className="info-value-token-address">
                  <div className="address-container">
                    <span className="token-address monospace">{position.token_address}</span>
                    <CopyBtn text={position.token_address} />
                    <button
                      className={`copy-btn ${copied ? 'copied' : ''}`}
                      onClick={copyAddress}
                      title="Copier l'adresse"
                    >
                      {copied ? '✓' : '📋'}
                    </button>
                  </div>
                  <div className="chain-badge-container">
                    <span className="chain-badge">{position.chain.toUpperCase()}</span>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-label">Quantité Actuelle</div>
                <div className="info-value">{position.amount.toFixed(6)} tokens</div>
              </div>

              <div className="info-card">
                <div className="info-label">Quantité Initiale</div>
                <div className="info-value">
                  {/* 🔧 FIX: Utiliser initial_amount directement */}
                  {(position.initial_amount || position.amount).toFixed(6)} tokens
                </div>
              </div>

              <div className="info-card">
                <div className="info-label">Investi (SOL)</div>
                <div className="info-value highlight">
                  {/* 🔧 FIX: Calculer l'investissement initial en SOL */}
                  {((position.initial_amount || position.amount) * position.entry_price).toFixed(6)} SOL
                </div>
              </div>

              <div className="info-card">
                <div className="info-label">Prix d'Entrée</div>
                <div className="info-value">{formatPrice(position.entry_price)}</div>
              </div>

              <div className="info-card">
                <div className="info-label">Prix Actuel</div>
                <div className="info-value">{formatPrice(position.current_price)}</div>
              </div>

              <div className="info-card">
                <div className="info-label">🛑 Stop Loss Configuré</div>
                <div className="info-value">{formatPrice(position.stop_loss)}</div>
              </div>

              <div className="info-card">
                <div className="info-label">💰 Take Profit Configuré</div>
                <div className="info-value">{formatPrice(position.take_profit)}</div>
              </div>

              {/* 🆕 Source de détection */}
              {position.source && (
                <div className="info-card">
                  <div className="info-label">📡 Source de Détection</div>
                  <div className="info-value source-badge">{formatSource(position.source)}</div>
                </div>
              )}
            </div>

            {/* 🆕 Section URL et Paramètres de Détection */}
            {(position.detection_url || position.detection_params) && (
              <div className="section">
                <h3 className="section-title">🔍 Informations de Détection</h3>

                {position.detection_url && (
                  <div className="detection-info-card">
                    <div className="info-label">🔗 URL de Détection</div>
                    <div className="detection-url-container">
                      <a
                        href={position.detection_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="detection-url-link"
                      >
                        {position.detection_url}
                      </a>
                    </div>
                  </div>
                )}

                {position.detection_params && Object.keys(position.detection_params).length > 0 && (
                  <div className="detection-info-card">
                    <div className="info-label">⚙️ Paramètres de Détection</div>
                    <div className="detection-params">
                      {Object.entries(position.detection_params).map(([key, value]) => (
                        <div key={key} className="param-item">
                          <span className="param-key">{key}:</span>
                          <span className="param-value">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* PnL de la position */}
            <div className="pnl-section">
              <div className="pnl-card">
                <div className="pnl-label">💰 PnL Total de la Position</div>
                <div className={`pnl-value ${(position.pnl_percent || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {/* 🔧 FIX: Utiliser pnl_usd de l'API qui est correctement calculé */}
                  {(position.pnl_usd || 0) >= 0 ? '+' : ''}{formatPrice(Math.abs(position.pnl_usd || 0))}
                  <span className="pnl-percent">
                    ({(position.pnl_percent || 0) >= 0 ? '+' : ''}{(position.pnl_percent || 0).toFixed(2)}%)
                  </span>
                </div>
                <div className="pnl-description">
                  {position.status === 'closed'
                    ? `PnL réalisé sur position fermée`
                    : `PnL non réalisé sur ${position.amount.toFixed(6)} tokens restants`
                  }
                </div>
              </div>
            </div>
          </div>

          {/* 🆕 NOUVEAU: Évaluation du Token */}
          {position.evaluation && (
            <div className="evaluation-sections">
              {/* Scores */}
              <section className="modal-section">
                <h3>📊 Évaluation du Token</h3>
                <div className="score-big">
                  <div className="score-value-big">{position.evaluation.score}</div>
                  <div className="score-label-big">/100</div>
                </div>
                <div className="recommendation-badge">
                  {position.evaluation.recommendation || 'N/A'}
                </div>
                <div className="info-grid">
                  {position.evaluation.anti_rug?.contract_verified && (
                    <div className="info-item">
                      <span className="info-label">Contract Verified</span>
                      <span className="info-value">
                        {position.evaluation.anti_rug.contract_verified.score || 0}/20
                        {' → '}
                        {position.evaluation.anti_rug.contract_verified.points?.toFixed(1) || '0.0'}/20 pts
                        {position.evaluation.anti_rug.contract_verified.score >= 15 ? ' ✅' : ' ❌'}
                      </span>
                    </div>
                  )}
                  {position.evaluation.anti_rug?.mint_control && (
                    <div className="info-item">
                      <span className="info-label">Mint Control</span>
                      <span className="info-value">
                        {position.evaluation.anti_rug.mint_control.score || 0}/25
                        {' → '}
                        {position.evaluation.anti_rug.mint_control.points?.toFixed(1) || '0.0'}/25 pts
                        {position.evaluation.anti_rug.mint_control.score >= 20 ? ' ✅' : ' ❌'}
                      </span>
                    </div>
                  )}
                  {position.evaluation.anti_rug?.lp_amount && (
                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="info-label">LP Amount</span>
                      <span className="info-value">
                        {position.evaluation.anti_rug.lp_amount.score || 0}/15
                        {' → '}
                        {position.evaluation.anti_rug.lp_amount.points?.toFixed(1) || '0.0'}/15 pts
                        {position.evaluation.anti_rug.lp_amount.score >= 10 ? ' ✅' : ' ❌'}
                      </span>
                      {position.evaluation.anti_rug.lp_amount.lp_usd != null && (
                        <div style={{ fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '4px' }}>
                          💰 LP: ${position.evaluation.anti_rug.lp_amount.lp_usd.toLocaleString()} USD
                        </div>
                      )}
                    </div>
                  )}
                  {position.evaluation.anti_rug?.lp_locked && (
                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="info-label">LP Locked</span>
                      <span className="info-value">
                        {position.evaluation.anti_rug.lp_locked.score || 0}/15
                        {' → '}
                        {position.evaluation.anti_rug.lp_locked.points?.toFixed(1) || '0.0'}/15 pts
                        {position.evaluation.anti_rug.lp_locked.score >= 10 ? ' ✅' : ' ❌'}
                      </span>
                      {position.evaluation.anti_rug.lp_locked.lock_days != null && (
                        <div style={{ fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '4px' }}>
                          🔒 Lock: {position.evaluation.anti_rug.lp_locked.lock_days} jours
                        </div>
                      )}
                    </div>
                  )}
                  {position.evaluation.anti_rug?.team_balance && (
                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="info-label">Team Balance</span>
                      <span className="info-value">
                        {position.evaluation.anti_rug.team_balance.score || 0}/10
                        {' → '}
                        {position.evaluation.anti_rug.team_balance.points?.toFixed(1) || '0.0'}/10 pts
                        {position.evaluation.anti_rug.team_balance.score >= 8 ? ' ✅' : ' ❌'}
                      </span>
                      {position.evaluation.anti_rug.team_balance.team_percent != null && (
                        <div style={{ fontSize: '0.9em', color: 'var(--text-muted)', marginTop: '4px' }}>
                          👥 Team détient: {position.evaluation.anti_rug.team_balance.team_percent.toFixed(1)}% du supply
                        </div>
                      )}
                    </div>
                  )}
                  {position.evaluation.anti_rug?.tradeability && (
                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="info-label">Tradeability</span>
                      <span className="info-value">
                        {position.evaluation.anti_rug.tradeability.score || 0}/10
                        {' → '}
                        {position.evaluation.anti_rug.tradeability.points?.toFixed(1) || '0.0'}/10 pts
                        {position.evaluation.anti_rug.tradeability.score >= 8 ? ' ✅' : ' ❌'}
                      </span>
                      {position.evaluation.anti_rug.tradeability.is_honeypot && (
                        <div style={{ fontSize: '0.9em', color: 'var(--color-danger)', marginTop: '4px', fontWeight: 'bold' }}>
                          🍯 HONEYPOT DÉTECTÉ!
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>

              {/* GoPlus Security */}
              {position.evaluation.goplus_security && (
                <section className="modal-section">
                  <h3>🔒 GoPlus Security</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Risk Level</span>
                      <span className="info-value">{position.evaluation.goplus_security.risk_level || 'N/A'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Honeypot</span>
                      <span className="info-value">
                        {position.evaluation.goplus_security.is_honeypot ? '⚠️ Oui' : '✅ Non'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Mintable</span>
                      <span className="info-value">
                        {position.evaluation.goplus_security.is_mintable ? '⚠️ Oui' : '✅ Non'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Open Source</span>
                      <span className="info-value">
                        {position.evaluation.goplus_security.is_open_source ? '✅ Oui' : '❌ Non'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Holders</span>
                      <span className="info-value">
                        {position.evaluation.goplus_security.holder_count?.toLocaleString() || 'N/A'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Confidence</span>
                      <span className="info-value">
                        {position.evaluation.goplus_security.confidence_score || 0}%
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Buy Tax</span>
                      <span className="info-value">
                        {((position.evaluation.goplus_security.buy_tax || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Sell Tax</span>
                      <span className="info-value">
                        {((position.evaluation.goplus_security.sell_tax || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Can Buy</span>
                      <span className="info-value">
                        {position.evaluation.goplus_security.cannot_buy ? '❌ Non' : '✅ Oui'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Can Sell All</span>
                      <span className="info-value">
                        {position.evaluation.goplus_security.cannot_sell_all ? '❌ Non' : '✅ Oui'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Total Liquidity</span>
                      <span className="info-value">
                        ${position.evaluation.goplus_security.total_liquidity?.toLocaleString() || 0}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Creator Balance</span>
                      <span className="info-value">
                        {position.evaluation.token_info?.team_balance_percent != null
                          ? `${position.evaluation.token_info.team_balance_percent.toFixed(1)}%`
                          : `${((position.evaluation.goplus_security.creator_percent || 0) * 100).toFixed(1)}%`
                        }
                      </span>
                    </div>
                  </div>
                </section>
              )}

              {/* Market Data */}
              {position.evaluation.token_info && (
                <section className="modal-section">
                  <h3>💰 Market Data</h3>
                  <div className="info-grid">
                    {position.evaluation.token_info.price_usd != null && (
                      <div className="info-item">
                        <span className="info-label">Prix</span>
                        <span className="info-value">${position.evaluation.token_info.price_usd}</span>
                      </div>
                    )}
                    {position.evaluation.token_info.market_cap != null && (
                      <div className="info-item">
                        <span className="info-label">Market Cap</span>
                        <span className="info-value">
                          ${position.evaluation.token_info.market_cap.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {position.evaluation.token_info.volume_24h != null && (
                      <div className="info-item">
                        <span className="info-label">Volume 24h</span>
                        <span className="info-value">
                          ${position.evaluation.token_info.volume_24h.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {position.evaluation.token_info.liquidity_usd != null && (
                      <div className="info-item">
                        <span className="info-label">Liquidité</span>
                        <span className="info-value">
                          ${position.evaluation.token_info.liquidity_usd.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* Statistiques des trades */}
          {summary && (
            <div className="stats-section">
              <h3>📈 Statistiques</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total Achats</div>
                  <div className="stat-value">{formatPrice(summary.total_buys_usd)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Total Ventes</div>
                  <div className="stat-value">{formatPrice(summary.total_sells_usd)}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">PnL Réalisé</div>
                  <div className={`stat-value ${summary.total_pnl_usd >= 0 ? 'positive' : 'negative'}`}>
                    {summary.total_pnl_usd >= 0 ? '+' : ''}{formatPrice(Math.abs(summary.total_pnl_usd))}
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Nombre de TPs</div>
                  <div className="stat-value">{summary.take_profits_triggered}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Nombre de SLs</div>
                  <div className="stat-value">{summary.stop_loss_triggered}</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">% Vendu</div>
                  <div className="stat-value">
                    {/* 🔧 FIX: Calculer le % vendu basé sur initial_amount et amount actuel */}
                    {(() => {
                      const initialAmount = position.initial_amount || position.amount;
                      const currentAmount = position.amount;
                      const percentSold = initialAmount > 0 ? ((initialAmount - currentAmount) / initialAmount) * 100 : 0;
                      return percentSold.toFixed(1);
                    })()}%
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Historique des trades */}
          <div className="trades-section">
            <h3>💱 Historique des Transactions</h3>
            {loading ? (
              <div className="loading">Chargement...</div>
            ) : trades.length === 0 ? (
              <div className="no-trades">Aucune transaction trouvée</div>
            ) : (
              <div className="trades-list">
                {trades.map((trade, index) => (
                  <div key={index} className={`trade-item ${trade.trade_type}`}>
                    <div className="trade-icon">{getTradeIcon(trade.trade_type)}</div>
                    <div className="trade-details">
                      <div className="trade-header">
                        <span className="trade-type">{getTradeLabel(trade)}</span>
                        <span className="trade-date">{formatDate(trade.executed_at)}</span>
                      </div>
                      <div className="trade-info">
                        <div className="trade-info-item">
                          <span className="label">Quantité:</span>
                          <span className="value">{trade.amount.toFixed(6)} tokens</span>
                        </div>
                        <div className="trade-info-item">
                          <span className="label">Prix:</span>
                          <span className="value">{formatPrice(trade.price)}</span>
                        </div>
                        <div className="trade-info-item">
                          <span className="label">Valeur:</span>
                          <span className="value">{formatPrice(trade.value_usd)}</span>
                        </div>
                        {trade.pnl_usd !== 0 && (
                          <div className="trade-info-item">
                            <span className="label">PnL:</span>
                            <span className={`value ${trade.pnl_usd >= 0 ? 'positive' : 'negative'}`}>
                              {trade.pnl_usd >= 0 ? '+' : ''}{formatPrice(Math.abs(trade.pnl_usd))}
                              ({trade.pnl_percent >= 0 ? '+' : ''}{trade.pnl_percent.toFixed(2)}%)
                            </span>
                          </div>
                        )}
                        {trade.remaining_amount && (
                          <div className="trade-info-item">
                            <span className="label">Restant:</span>
                            <span className="value">{trade.remaining_amount.toFixed(6)} tokens</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PositionDetailModal;

